// ============================================
// Investment Platform - TypeScript Types
// ============================================

export type AssetType =
  | 'STOCK'
  | 'FII'
  | 'ETF_NATIONAL'
  | 'ETF_INTERNATIONAL'
  | 'FUND'
  | 'CRYPTO'
  | 'EMERGENCY_RESERVE';

export type TransactionType = 'BUY' | 'SELL';
export type DividendType = 'DIVIDEND' | 'JCP' | 'AMORTIZATION' | 'RENDIMENTO';
export type AlertType = 'PRICE_LOW' | 'PRICE_HIGH' | 'DIVIDEND_COMING';

// ============================================
// User
// ============================================
export type UserRole = 'superadmin' | 'admin' | 'user';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string | null;
  avatarUrl?: string;
  preferences: Record<string, unknown>;
  createdAt?: string;
}

export interface Tenant {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  settings?: Record<string, unknown>;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ============================================
// Assets
// ============================================
export interface Asset {
  id: string;
  userId: string;
  code: string;
  name: string;
  type: AssetType;
  sector?: string;
  quantity: number;
  averagePrice: number;
  currentPrice?: number;
  currency: string;
  broker?: string;
  notes?: string;
  firstPurchaseDate: string;
  lastUpdatedPrice?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssetPerformance {
  asset: Asset;
  performance: {
    totalInvested: number;
    currentValue: number;
    gainLoss: number;
    gainLossPercent: number;
    currentPrice: number;
    averagePrice: number;
    quantity: number;
  };
  transactions: Transaction[];
}

// ============================================
// Transactions
// ============================================
export interface Transaction {
  id: string;
  assetId: string;
  userId: string;
  type: TransactionType;
  quantity: number;
  price: number;
  totalValue: number;
  fees: number;
  transactionDate: string;
  broker?: string;
  notes?: string;
  createdAt: string;
}

// ============================================
// Dividends
// ============================================
export interface Dividend {
  id: string;
  assetId: string;
  userId: string;
  valuePerShare: number;
  totalValue: number;
  paymentDate: string;
  exDate?: string;
  recordDate?: string;
  type: DividendType;
  isConfirmed: boolean;
  createdAt: string;
  assets?: {
    code: string;
    name: string;
    type: AssetType;
  };
}

export interface DividendCalendarMonth {
  month: string;
  monthNumber: number;
  dividends: Dividend[];
  total: number;
}

export interface DividendProjection {
  historicalAvgMonthly: number;
  historicalAvgAnnual: number;
  projection: Array<{
    month: string;
    monthName: string;
    projected: number;
  }>;
  totalProjected: number;
}

// ============================================
// Portfolio
// ============================================
export interface PortfolioSummary {
  totalInvested: number;
  currentValue: number;
  gainLoss: number;
  gainLossPercent: number;
  assetCount: number;
  byType: Array<{
    type: AssetType;
    invested: number;
    current: number;
    percentage: number;
    count: number;
  }>;
  topPerformers: PerformanceItem[];
  worstPerformers: PerformanceItem[];
  recentDividends: Dividend[];
  activeAlerts: Alert[];
}

export interface PerformanceItem {
  code: string;
  name: string;
  type: AssetType;
  gainLossPercent: number;
  gainLoss: number;
}

export interface AllocationItem {
  type: AssetType;
  value: number;
  percentage: number;
}

export interface PortfolioHistoryPoint {
  month: string;
  invested: number;
  withdrawn: number;
  net: number;
}

// ============================================
// Goals
// ============================================
export interface Goal {
  id: string;
  userId: string;
  assetType: AssetType;
  targetPercentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface GoalProgress {
  goals: Goal[];
  currentAllocation: AllocationItem[];
  totalValue: number;
  rebalancing: Array<{
    type: AssetType;
    targetPercentage: number;
    currentPercentage: number;
    diff: number;
    diffValue: number;
    action: 'COMPRAR' | 'VENDER' | 'OK';
  }>;
  isBalanced: boolean;
}

// ============================================
// Alerts
// ============================================
export interface Alert {
  id: string;
  userId: string;
  assetId?: string;
  alertType: AlertType;
  thresholdValue?: number;
  message?: string;
  isActive: boolean;
  triggeredAt?: string;
  createdAt: string;
  updatedAt: string;
  assets?: {
    code: string;
    name: string;
    currentPrice: number;
    type: AssetType;
  };
}

// ============================================
// Market Data
// ============================================
export interface QuoteData {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume?: number;
  high?: number;
  low?: number;
  open?: number;
  previousClose?: number;
  updatedAt: string;
}

// ============================================
// API Response
// ============================================
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

export interface ApiError {
  success: false;
  statusCode: number;
  message: string;
  error: string;
}

// ============================================
// Forms
// ============================================
export interface LoginForm {
  email: string;
  password: string;
}

export interface RegisterForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export type InvestorProfileType = 'CONSERVADOR' | 'MODERADO' | 'ARROJADO' | 'AGRESSIVO';

export interface InvestorProfile {
  userId: string;
  investmentObjective: number;
  investmentHorizon: number;
  lossReaction: number;
  financialKnowledge: number;
  monthlyIncome: number;
  investmentPercentage: number;
  variableIncomeExperience: number;
  score: number;
  profileType: InvestorProfileType;
  filledAt: string;
}

export interface UserProfile {
  userId: string;
  phone: string;
  cpf: string;
  birthDate: string;
  addressZip: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement?: string;
  addressNeighborhood: string;
  addressCity: string;
  addressState: string;
}

export interface RequestRegistrationForm {
  email: string;
}

export interface CompleteRegistrationForm {
  token: string;
  // Dados pessoais
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  cpf: string;
  birthDate: string;
  // Endereço
  addressZip: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement?: string;
  addressNeighborhood: string;
  addressCity: string;
  addressState: string;
  // Perfil do investidor
  investmentObjective: number;
  investmentHorizon: number;
  lossReaction: number;
  financialKnowledge: number;
  monthlyIncome: number;
  investmentPercentage: number;
  variableIncomeExperience: number;
}

export interface AssetForm {
  code: string;
  name: string;
  type: AssetType;
  sector?: string;
  quantity: number;
  averagePrice: number;
  broker?: string;
  firstPurchaseDate: string;
  currency?: string;
  notes?: string;
}

export interface TransactionForm {
  type: TransactionType;
  quantity: number;
  price: number;
  fees?: number;
  transactionDate: string;
  broker?: string;
  notes?: string;
}

// ============================================
// Labels & Colors
// ============================================
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  STOCK: 'Ações',
  FII: 'FIIs',
  ETF_NATIONAL: 'ETFs Nacionais',
  ETF_INTERNATIONAL: 'ETFs Internacionais',
  FUND: 'Fundos',
  CRYPTO: 'Criptomoedas',
  EMERGENCY_RESERVE: 'Reserva de Emergência',
};

export const ASSET_TYPE_COLORS: Record<AssetType, string> = {
  STOCK: '#3b82f6',
  FII: '#10b981',
  ETF_NATIONAL: '#f59e0b',
  ETF_INTERNATIONAL: '#8b5cf6',
  FUND: '#ec4899',
  CRYPTO: '#f97316',
  EMERGENCY_RESERVE: '#6b7280',
};

export const ASSET_TYPE_BG: Record<AssetType, string> = {
  STOCK: 'bg-blue-500/20 text-blue-400',
  FII: 'bg-emerald-500/20 text-emerald-400',
  ETF_NATIONAL: 'bg-amber-500/20 text-amber-400',
  ETF_INTERNATIONAL: 'bg-violet-500/20 text-violet-400',
  FUND: 'bg-pink-500/20 text-pink-400',
  CRYPTO: 'bg-orange-500/20 text-orange-400',
  EMERGENCY_RESERVE: 'bg-gray-500/20 text-gray-400',
};
